<template>
  <SectionTab
    :title="$t('reporting.industry.inventory')"
    :description="$t('reporting.industry.inventoryOverview')"
    :date="date"
  >
    <template v-slot:section>
      <LoadingSpinner v-if="loading" />
      <div v-else>
        <ListItem v-for="(areaData, areaIndex) in inventoryData" :key="areaData.area_uuid" class="card card--sub">
          <template v-slot:row-cols>
            <div class="card__header">
              <span class="card__title">{{ areaTitle(areaData) }}</span>
            </div>
          </template>
          <template v-slot:toggle-area>
            <p v-if="emptyTable(areaData)" class="alert alert-info">{{ $t('reporting.industry.noData') }}</p>
            <div v-else class="panel">
              <div
                class="panel__table"
                :class="{
                  'panel__table--double': tableRowWidth(areaData),
                  'panel__table--empty': emptyTable(areaData),
                }"
              >
                <div v-for="(total, type_name) in areaData.type_group_names" :key="type_name" class="row-type">
                  <span class="row-type__text" :title="type_name">{{ type_name }}</span>
                  <span class="row-type__number">{{ total }}</span>
                </div>
              </div>
              <div class="panel__graph" :class="{ empty: areaData.container_count == 0 }">
                <div class="graph" v-if="areaData.container_count > 0">
                  <span class="title">{{ $t('reporting.industry.containerDistribution') }}</span>
                  <e-chart :option="areaChartOptions(areaData, areaIndex)" :autoresize="true" ref="chart"> </e-chart>
                </div>
              </div>
            </div>
          </template>
        </ListItem>
      </div>
    </template>
  </SectionTab>
</template>

<script>
import defaultStyle from 'echarts/lib/model/globalDefault'

import ListItem from '@/components/ListItems/index.vue'
import LoadingSpinner from '@/components/LoadingSpinner/index.vue'
import SectionTab from '@/components/Reporting/Industry/SectionTab'

import reportingApi from '@/api/reporting.js'

export default {
  name: 'Inventory',
  props: {
    companyUuid: { type: String, required: true },
  },
  components: { ListItem, LoadingSpinner, SectionTab },
  data() {
    return {
      loading: true,
      inventoryData: null,
      updatedAt: null,
    }
  },
  computed: {
    date() {
      if (!this.updatedAt) return ''
      return this.$t('reporting.industry.lastDate', { date: this.updatedAt })
    },
    chartOptions() {
      return {
        title: {
          left: 'center',
          top: '33%',
          text: 'zone',
          subtext: this.$t('meta.container'),
          padding: 0,
        },
        legend: {
          bottom: '10%',
          type: 'scroll',
          left: 'center',
          padding: [0, 20],
          data: [],
        },
        tooltip: { trigger: 'item' },
        series: {
          type: 'pie',
          center: ['50%', '40%'],
          data: [],
          labelLine: { show: false },
          radius: ['40%', '60%'],
          itemStyle: {
            borderRadius: 10,
            borderColor: '#f1f1f1',
            borderWidth: 2,
          },
        },
      }
    },
  },
  methods: {
    emptyTable(data) {
      return !Object.keys(data.type_group_names).length
    },
    areaTitle(data) {
      return `${data.area_name} (${data.container_count} ${this.$t('meta.container')})`
    },
    tableRowWidth(data) {
      return Object.keys(data.type_group_names).length > 7
    },
    areaZoneFormat(data, areaIndex) {
      const colorList = defaultStyle.color
      const color = i => colorList[(i + areaIndex) % colorList.length]

      return Object.keys(data.zones).map((zone, index) => ({
        value: data.zones[zone],
        name: zone,
        label: {
          show: data.zones[zone] > 0,
          formatter: '{c}',
          distanceToLabelLine: -20,
        },
        itemStyle: { color: color(index) },
      }))
    },
    areaChartOptions(data, areaIndex) {
      const options = { ...this.chartOptions }
      const zoneFormat = this.areaZoneFormat(data, areaIndex)

      options.title.text = data.container_count
      options.series.data = zoneFormat
      options.legend.data = zoneFormat.sort((a, b) => b.value - a.value)
      return options
    },
    async loadSelectedCompanyData() {
      this.loading = true
      const response = await reportingApi.getAreaStats(this.companyUuid)
      this.inventoryData = response.areas
      this.updatedAt = response.updated_at
      this.loading = false
    },
  },
  watch: {
    async companyUuid() {
      await this.loadSelectedCompanyData()
    },
  },
  async mounted() {
    await this.loadSelectedCompanyData()
  },
}
</script>

<style lang="scss" scoped>
@import '../panel.scss';

.card {
  &.card--sub {
    background-color: #f1f1f1;
    padding: 0.5rem;
    margin-left: 0.5rem;
    margin-top: 0.5rem;
  }

  &__title {
    font-weight: 600;
    font-size: 1.125rem;
  }
}

.panel {
  display: flex;
  min-height: 12rem;
  margin: 1rem 0 1rem 1rem;

  &__table {
    flex: 3;
    border-radius: var(--border-radius-m);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-height: 19rem;
    flex-wrap: wrap;
    overflow: hidden;
    height: 100%;
    margin-bottom: 2rem;

    &--double {
      .row-type {
        max-width: calc(50% - 0.25rem);
      }
    }

    &--empty {
      height: auto;
      align-items: center;
      justify-content: center;
      color: #fff;
    }

    .row-type {
      background-color: #fff;
      border-radius: var(--border-radius-m);
      min-height: 2rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.5rem 0.75rem;
      font-size: 1.125rem;

      &__text {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &__number {
        margin-left: 0.75rem;
      }
    }
  }

  &__graph {
    flex: 2;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    align-self: flex-end;

    .title {
      position: absolute;
      left: 50%;
      transform: translate(-50%, -50%);
    }
    &.empty {
      align-self: center;
      padding: 0 1rem;
    }

    .graph {
      height: 16rem;
    }
  }
}
</style>
